import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ChirpConfigs, Devices, TrackerConfigs } from "@app/shared/src/api/api.gen";
import {
    ChirpConfig,
    Device,
    EventTrigger,
    EventTriggerType,
    TrackerConfig,
    getDeviceTypeDisplayName,
    getOperationModeDisplayName,
} from "@app/shared/src/models";
import {
    Btn,
    Card,
    CardHead,
    CardNote,
    FRow,
    FVal,
    Note,
    Segmented,
    Spinner,
    useToast,
} from "@app/shared/src/components_v2";
import { sideOfZoneName } from "@app/shared/src/helpers/zoneNameHelpers";
import { BackLink } from "../../helpers/backLink";
import { useFleetDefaultsRecord } from "../../hooks/useFleetDefaultsRecord";
import { urlEnum, useUrlState } from "../../hooks/useUrlState";
import { useFleet } from "../providers/FleetProvider";
import { AlertsRelaysSection } from "../sections/AlertsRelaysSection";
import { DriverAccessSection } from "../sections/DriverAccessSection";
import { NotificationsSection } from "../sections/NotificationsSection";
import { ProtectedZoneSection } from "../sections/ProtectedZoneSection";
import { RadarVideoSection } from "../sections/RadarVideoSection";
import { CabCardSection } from "../sections/CabCardSection";
import { DeviceStatusTag } from "../shared/DeviceStatusTag";
import { StatusDot } from "../shared/StatusDot";
import { UpgradePackagesDialog } from "../shared/UpgradePackagesDialog";

/**
 * The persistent half of the driver/director contract, organised the way a director thinks about
 * it: what the unit is, what it fires on, who hears about it, and the cab card that hands the
 * driver their slice of control.
 *
 * Identity and mounting geometry are shown read-only: no API exposes writing them, and a field that
 * looks editable but silently discards the change is worse than one that says where the edit lives.
 * Two exceptions: the protected zone's footprint is edited in place (ProtectedZoneSection — a TMA
 * is measured per truck, not per fleet), and Firmware's Update opens a package picker whose choice
 * becomes a real queued command (the same apt path as the main dashboard's Upgrade tab).
 */

type TabKey = "setup" | "alerts" | "radar" | "notify" | "driver" | "cab";

const TAB_KEYS = ["setup", "alerts", "radar", "notify", "driver", "cab"] as const;
const TAB_CODEC = urlEnum<TabKey>(TAB_KEYS);

const TABS: { key: TabKey; label: string }[] = [
    { key: "setup", label: "Setup" },
    { key: "alerts", label: "Alerts & Relays" },
    { key: "radar", label: "Radar & video" },
    { key: "notify", label: "Notifications" },
    { key: "driver", label: "Driver access" },
    { key: "cab", label: "Driver QR code" },
];

const M_TO_FT = 3.28084;
const ft = (metres: number | undefined | null) => (metres == null ? "—" : `${(metres * M_TO_FT).toFixed(1)} ft`);

export const DeviceSettingsPage: React.FC = () => {
    const { deviceId } = useParams<{ deviceId: string }>();
    // Org-wide: this page is reached by a link that may point outside the current group filter.
    const { allDevices, loadTriggers, reload } = useFleet();
    // Usually reached from the truck's own page, but an incident report links straight in — and
    // sends the reader back to the report they were reading, not to a page they never saw.
    const location = useLocation() as { state?: BackLink };
    const toast = useToast();
    // In the URL so a truck's Notifications tab is a link you can send, and so Back steps out of a
    // tab instead of leaving the whole settings page.
    const [tab, setTab] = useUrlState<TabKey>("tab", "setup", TAB_CODEC);
    const [triggers, setTriggers] = useState<EventTrigger[]>();
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    // The preset catalogues the radar pickers offer. Failing to load one leaves its picker empty
    // rather than breaking the page — a truck's other settings are still worth showing.
    const [chirpConfigs, setChirpConfigs] = useState<ChirpConfig[]>([]);
    const [trackerConfigs, setTrackerConfigs] = useState<TrackerConfig[]>([]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            ChirpConfigs.getAll().catch(() => [] as ChirpConfig[]),
            TrackerConfigs.getAll().catch(() => [] as TrackerConfig[]),
        ]).then(([chirps, trackers]) => {
            if (cancelled) return;
            setChirpConfigs(chirps ?? []);
            setTrackerConfigs(trackers ?? []);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const device = allDevices.find((d) => d.id === deviceId);

    /**
     * The device's complete record. The org roster deliberately travels light — it is polled every
     * minute and omits the sensor config with its inlined calibration and tracker JSON — so the
     * radar tab read nothing but blanks off it, and would have posted those blanks back on save.
     * The tabs that read or write the whole device get this copy instead, refetched after a save so
     * the section's "saved" baseline moves with what was written.
     */
    const [fullDevice, setFullDevice] = useState<Device>();
    const refreshFullDevice = useCallback(async () => {
        if (!deviceId) return;
        try {
            setFullDevice(await Devices.getById(deviceId));
        } catch {
            // The roster still identifies the unit; the radar tab shows its own spinner until a
            // retry (the next save or a navigation) gets a copy.
        }
    }, [deviceId]);

    // Reset only when the unit changes — the stamp-driven refreshes below swap the copy in place
    // rather than blanking the tab back to a spinner.
    useEffect(() => setFullDevice(undefined), [deviceId]);

    /**
     * Re-read the full record whenever the roster says this unit's row moved. SettingsRevision
     * advances on every settings write — the device row or its triggers — and LastModified on any
     * dashboard edit, so a section's own save (through reload) and another user's change (through
     * the minute poll) both land here, while an unchanged poll cycle refetches nothing.
     */
    const deviceStamp = device ? `${device.settingsRevision}·${device.lastModified?.getTime() ?? ""}` : undefined;
    useEffect(() => {
        if (deviceStamp === undefined) return;
        refreshFullDevice();
    }, [refreshFullDevice, deviceStamp]);

    useEffect(() => {
        if (!device?.facilityId || allDevices.length === 0) return;
        let cancelled = false;
        loadTriggers().then((byFacility) => {
            if (!cancelled) setTriggers(byFacility.get(device.facilityId!) ?? []);
        });
        return () => {
            cancelled = true;
        };
    }, [device?.facilityId, allDevices, loadTriggers]);

    const collision = useMemo(
        () => triggers?.find((t) => t.triggerType === EventTriggerType.ImpendingZoneEntry && !sideOfZoneName(t.detectionZone?.name)),
        [triggers],
    );


    /**
     * The organization's stated defaults, handed to each section so it can decide for itself whether
     * this unit still follows them. Inheritance is derived by comparing values rather than stored as
     * a flag, so a badge can never disagree with what is printed underneath it.
     */
    const fleetDefaults = useFleetDefaultsRecord();

    // Package selection happens in the dialog; this only has to know the request went through.
    const onUpgradeQueued = () => {
        toast("Update queued — the unit installs it on its next check-in");
        reload();
    };

    if (!device) {
        return allDevices.length === 0 ? (
            <div className="grid place-items-center p-12 text-[var(--muted)]">
                <Spinner />
            </div>
        ) : (
            <Note tone="crit">That unit isn't in this organization.</Note>
        );
    }

    const vehicle = getOperationModeDisplayName(device.mode) ?? "—";

    return (
        <div className="flex flex-col gap-4">
            <Link
                to={location.state?.from ?? `/devices/${device.id}`}
                state={location.state?.fromState}
                className="text-[12.5px] font-semibold text-[var(--muted)]"
            >
                ← {location.state?.fromLabel ?? `Back to ${device.name}`}
            </Link>

            <div className="flex flex-wrap items-end gap-4">
                <div>
                    <h1 className="flex items-center gap-3 text-[28px] font-bold tracking-tight">
                        <StatusDot status={device.status} lastHeartbeat={device.lastHeartbeat} />
                        {device.name}
                    </h1>
                    <div className="mt-1.5 text-[13px] text-[var(--muted)]">
                        {vehicle} · key <span className="font-mono">{device.deviceKey}</span> · firmware{" "}
                        {device.firmwareVersion ?? "—"}
                    </div>
                </div>
                <span className="ml-auto">
                    <DeviceStatusTag status={device.status} lastHeartbeat={device.lastHeartbeat} />
                </span>
            </div>

            <Segmented full tone="accent" options={TABS} value={tab} onChange={setTab} />

            {tab === "setup" && (
                <>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card>
                            <CardHead title="Identity" />
                            <div className="mt-2">
                                <FRow title="Truck / TMA number">
                                    <FVal>{device.name}</FVal>
                                </FRow>
                                <FRow title="Device key" sub="From the label on the unit">
                                    <FVal mu mono>
                                        {device.deviceKey}
                                    </FVal>
                                </FRow>
                                <FRow title="Device type" sub="What this unit is deployed as">
                                    <FVal mu>{vehicle}</FVal>
                                </FRow>
                                <FRow title="Hardware">
                                    <FVal mu>{getDeviceTypeDisplayName(device.type) ?? "—"}</FVal>
                                </FRow>
                                <FRow
                                    title="Firmware"
                                    sub={
                                        device.hasPendingDeviceCommands
                                            ? "A command is waiting for the next check-in"
                                            : "Installs on the next check-in"
                                    }
                                >
                                    <FVal mu>{device.firmwareVersion ?? "—"}</FVal>
                                    <Btn tone="quiet" onClick={() => setUpgradeOpen(true)}>
                                        Update
                                    </Btn>
                                </FRow>
                            </div>
                        </Card>
                        <Card>
                            <CardHead title="Network" />
                            <div className="mt-2">
                                <FRow title="IP address">
                                    <FVal mu mono>
                                        {device.ipv4Address ?? "—"}
                                    </FVal>
                                </FRow>
                                <FRow title="Status">
                                    <DeviceStatusTag status={device.status} lastHeartbeat={device.lastHeartbeat} />
                                </FRow>
                                <FRow title="Site">
                                    <FVal mu>{device.facilityName ?? "—"}</FVal>
                                </FRow>
                            </div>
                        </Card>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card>
                            <CardHead title="Mounting" />
                            <div className="mt-2">
                                <FRow title="Height" sub="Ground to the unit">
                                    <FVal mu>{ft(device.height)}</FVal>
                                </FRow>
                                {/* The same value the cab screen reports — the driver API maps it straight
                                    from positionX, so the two surfaces can never disagree. */}
                                <FRow title="Offset from centre" sub="As reported to the cab screen">
                                    <FVal mu>{ft(device.positionX)}</FVal>
                                </FRow>
                                <FRow title="Heading">
                                    <FVal mu>{device.headingDegrees != null ? `${Math.round(device.headingDegrees)}°` : "—"}</FVal>
                                </FRow>
                            </div>
                            <CardNote>
                                Mounting geometry is set during onboarding and edited on the main dashboard — it recalibrates the
                                radar, which is not a casual change.
                            </CardNote>
                        </Card>
                        <ProtectedZoneSection
                            device={device}
                            collisionZone={collision?.detectionZone}
                            defaults={fleetDefaults.defaults}
                            onSaved={reload}
                        />
                    </div>
                </>
            )}

            {tab === "alerts" && (
                <AlertsRelaysSection
                    device={device}
                    triggers={triggers}
                    defaults={fleetDefaults.defaults}
                    onSaved={reload}
                />
            )}

            {/* This tab reads and writes the whole device row, so it waits for the full record —
                rendering it off the roster copy is what showed every preset as "not set". */}
            {tab === "radar" &&
                (fullDevice ? (
                    <RadarVideoSection
                        device={fullDevice}
                        defaults={fleetDefaults.defaults}
                        chirpConfigs={chirpConfigs}
                        trackerConfigs={trackerConfigs}
                        onSaved={reload}
                    />
                ) : (
                    <Card>
                        <div className="grid place-items-center p-12 text-[var(--muted)]">
                            <Spinner />
                        </div>
                    </Card>
                ))}

            {tab === "driver" && <DriverAccessSection defaults={fleetDefaults.defaults} />}

            {tab === "notify" && (
                <NotificationsSection
                    device={device}
                    triggers={triggers}
                    defaults={fleetDefaults.defaults}
                    onSaved={reload}
                />
            )}

            {tab === "cab" && (
                <>
                    <CabCardSection device={device} />
                    <Note>
                        Scanning opens this truck only, with no login. What a driver may change once scanned is set for the whole
                        organization under <b className="font-semibold text-[var(--text)]">Settings → Driver access</b>, and every
                        in-cab change reverts when the unit restarts.
                    </Note>
                </>
            )}

            <UpgradePackagesDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} device={device} onQueued={onUpgradeQueued} />
        </div>
    );
};
